import { useCallback, useEffect, useRef, useState } from 'react';

export const useSlideshow = (selectedImage: string, images: string[], onNavigate?: (src: string) => void) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    if (!selectedImage) {
      return;
    }
    setSelectedIndex(images.indexOf(selectedImage));
  }, [images, selectedImage]);

  const previous = useCallback(() => {
    const newIndex = (selectedIndex - 1 + images.length) % images.length;
    setSelectedIndex(newIndex);
    onNavigate?.(images[newIndex]);
  }, [images, selectedIndex, onNavigate]);

  const next = useCallback(() => {
    const newIndex = (selectedIndex + 1) % images.length;
    setSelectedIndex(newIndex);
    onNavigate?.(images[newIndex]);
  }, [images, selectedIndex, onNavigate]);

  const handleTouchStart = (e: React.TouchEvent) => {
    // Only handle single touch for navigation, prevent multi-touch gestures
    if (e.touches.length !== 1) {
      e.preventDefault();
      return;
    }
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Prevent multi-touch gestures (like pinch-to-zoom) outside of canvas
    if (e.touches.length > 1) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Only handle single touch for navigation
    if (e.changedTouches.length !== 1) {
      return;
    }

    touchEndX.current = e.changedTouches[0].screenX;
    const swipeDistance = touchStartX.current - touchEndX.current;

    // Only trigger navigation for significant horizontal swipes, measuring at 1/2 of the screen width
    const SWIPE_THRESHOLD_PIXELS = window.innerWidth / 2;
    if (Math.abs(swipeDistance) > SWIPE_THRESHOLD_PIXELS) {
      if (swipeDistance > 0) {
        next();
      } else {
        previous();
      }
    }
  };

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          previous();
          return;
        case 'ArrowRight':
          next();
          return;
        default:
          return;
      }
    };
    window.addEventListener('keydown', handle);
    return () => {
      window.removeEventListener('keydown', handle);
    };
  }, [next, previous]);

  const currentImage = images[selectedIndex] || images[0];

  return { currentImage, handleTouchEnd, handleTouchMove, handleTouchStart, next, previous, selectedIndex };
};
